import { QueryHookOptions } from '@apollo/client/react/types/types';
import { MEMBERSHIP_SUPPORT_GROUP_ID } from '@provision/conversation-constants';
import {
  ConversationChannelType,
  ConversationChatApp,
  ConversationParticipantEntity,
  ConversationParticipationMode,
  ConversationSmsDirection,
  ConversationStatus,
} from '@provision/conversation-types';
import {
  ApolloCache,
  gql,
  NetworkStatus,
  useMutation,
  useQuery,
} from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { isNullish } from 'remeda';
import {
  Partner,
  useConversationAssigneeGroups,
  useCurrentUserProfile,
  useEntityAssignments,
  useGroupRules,
  usePartners,
  usePlatformUserIds,
  UserGroupRules,
} from 'src/graphql';
import { useConversationAggregations } from 'src/graphql/entities/conversation-aggregations';
import {
  GetConversationMessageQuery,
  getConversationMessagesQueryVariables,
  useConversationMessages,
  useConversationPrivateMessageUpdate,
} from 'src/graphql/entities/conversation-message';
import { GET_CONVERSATION_MESSAGES_QUERY } from 'src/graphql/entities/conversation-message/conversation-message.operations';
import { useConversationMessageUpdate } from 'src/graphql/entities/conversation-message/hooks/use-conversation-message-update';
import { useCustomViews } from 'src/graphql/entities/custom-view';
import { ConversationAttribute } from 'src/graphql/entities/custom-view/custom-view-config/conversation';
import { resolveCustomAttributeSortValue } from 'src/graphql/entities/custom-view/custom-view-config/custom-attributes-bridge/column-renderers';
import { META_DATA_SEARCH_QUERY_FIELDS } from 'src/graphql/entities/metadata.fragments';
import {
  ConditionInput,
  EntityNames,
  QueryParams,
  QueryVariables,
  Records,
  SearchInput,
  SortingInput,
} from 'src/graphql/types';
import { generateFieldModifiersMap, useLoadMore } from 'src/graphql/utils';
import { useSelectedViewUrlContext } from 'src/pages/CustomViews/custom-views-selected-view/hooks/use-selected-view-url-context';
import { useCustomViewState } from 'src/pages/CustomViews/hooks/use-custom-view-state';
import { useGlobalStore } from 'src/store';
import useConversationViewsStore from 'src/store/conversation-views';
import { MappedUserData } from 'src/store/current-user';
import { ConversationRaw } from 'src/types';
import { getChatIdentifier } from 'src/utils/conversation';
import { useNotifications } from 'src/utils/hooks';
import { AppEvents, emitAppEvent } from 'src/utils/hooks/use-app-events';
import { useCacheManager } from 'src/utils/hooks/use-cache-manager';
import { useSnackbar } from 'src/utils/hooks/use-snackbar';
import { isSameRecords, removeNotDefinedProperties } from 'src/utils/objects';
import { sortByInput } from 'src/utils/sorting';
import { buildGetConversationVariables } from './conversation-query-variables';
import {
  CONVERSATION_ATTACHMENT_FIELDS,
  CONVERSATION_FIELDS,
  CONVERSATION_QUERY_FIELDS,
  CREATE_CONVERSATION_QUERY_FIELDS,
  SUBMIT_CONVERSATION_QUERY_FIELDS,
} from './conversation.fragments';
import { formatConversation } from './conversation.mappings';
import {
  Conversation,
  ConversationDataForSubmission,
  ConversationType,
  SubmitConversationDataChannel,
  SubmitConversationDataParticipant,
} from './conversation.types';
import { useInvalidateSiteConversations } from './use-invalidate-site-conversations';
import { useRequesterGetters } from './use-requester-getters';

export const buildGetConversationQuery = (includeCustomAttributes = false) => gql`
  query GetConversation(
    $params: QueryParamsWithSearch
    $skipTotalRecords: Boolean = false
    $includeAttachments: Boolean = false
  ) @entity(name: "Conversation") {
    getConversation(queryParams: $params) {
      records {
        ...ConversationQueryFields
        attachments @include(if: $includeAttachments) {
          ...ConversationAttachmentFields
        }
        ${includeCustomAttributes ? 'customAttributes' : ''}
      }
      metadata {
        ...MetaDataSearchQueryFields
      }
    }
  }
  ${META_DATA_SEARCH_QUERY_FIELDS}
  ${CONVERSATION_QUERY_FIELDS}
  ${CONVERSATION_ATTACHMENT_FIELDS}
`;

export const GET_CONVERSATION_QUERY = buildGetConversationQuery();

export type GetConversationQuery = {
  getConversation: Records<ConversationRaw>;
};

export const useConversations = (options?: {
  filter: ConditionInput[];
  sort?: SortingInput | SortingInput[];
  limit?: number;
  skip?: boolean;
  searchParams?: SearchInput;
  includeDeleted?: boolean;
  includeAttachments?: boolean;
  dynamicAttributePaths?: string[];
}) => {
  const { rawPartnerMap } = usePartners();

  const queryOptions = useMemo<QueryHookOptions>(
    () => ({
      variables: buildGetConversationVariables(options ?? {}),
      skip: options?.skip,
      notifyOnNetworkStatusChange: true,
    }),
    [options],
  );

  // add the customAttributes blob to the selection only when a grid view shows custom attribute
  // columns; otherwise the document is the shared no-custom-attribute query (dormant).
  const query = useMemo(
    () => buildGetConversationQuery(Boolean(options?.dynamicAttributePaths?.length)),
    [options?.dynamicAttributePaths],
  );

  const { viewId } = useSelectedViewUrlContext(EntityNames.Conversation);

  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { customViews } = useCustomViews({
    entity: EntityNames.Conversation,
    partnerId: selectedPartnerId,
  });

  const customView = useMemo(
    () => customViews.find((v) => v.customViewId === viewId),
    [customViews, viewId],
  );

  // if the hook runs outside of a view context, it won’t fetch any view data;
  // otherwise, it will verify the view column exists.
  // ideally the useConversations hook must stay agnostic of custom view relations,
  // but I decided to keep things simple in matter of a quick-fix solution
  const { columns } = useCustomViewState({
    id: viewId,
    customView,
    entity: EntityNames.Conversation,
  });

  const hasLabels = columns?.some(
    (column) => column.attribute === ConversationAttribute.label,
  );

  const useQueryResult = useQuery<GetConversationQuery, QueryVariables>(query, queryOptions);

  const {
    data,
    previousData,
    client,
    fetchMore,
    refetch,
    networkStatus,
    variables,
    loading: conversationsLoading,
    ...rest
  } = useQueryResult;

  const cacheManager = useCacheManager(query, useQueryResult);

  const conversationIds = useMemo(
    () => data?.getConversation.records.map((i) => i.conversationId).toSorted() ?? [],
    [data?.getConversation.records],
  );

  const { conversationAggregationsMap } = useConversationAggregations({
    conversationIds,
  });

  const { data: activeUserRecordsIds } = usePlatformUserIds({
    partnerId: selectedPartnerId,
    filters: [
      {
        attribute: 'isActive',
        operator: '=',
        value: 'true',
      },
    ],
  });

  const { friendlyIdToUuidMap, addFriendlyIdToUuidItem } = useConversationViewsStore();
  const { groupRules } = useGroupRules();
  const { rawConversationAssigneeGroupsMap } =
    useConversationAssigneeGroups(selectedPartnerId);

  const canAccessToPrivateLabels = groupRules[UserGroupRules.AccessToPrivateLabels];

  const {
    getAgent,
    getClient,
    loading: usersLoading,
  } = useRequesterGetters(data?.getConversation?.records);

  const { labelAssignmentsMap } = useEntityAssignments({
    entity: 'Conversation',
    ids: conversationIds,
    skip: !groupRules || (!hasLabels && options?.limit !== 1),
    includeDeleted: options?.includeDeleted,
  });

  const conversations = useMemo<Conversation[]>(() => {
    if (!data?.getConversation || usersLoading) {
      return [];
    }

    // server-side sorting is used by default, but client-side re-sorting lets us
    // skip refetching the whole query when a conversation updates
    return sortByInput<ConversationRaw>(
      data.getConversation.records,
      variables?.params?.sorting,
      resolveCustomAttributeSortValue,
    ).map((conversation) => {
      const formattedConversation = formatConversation(conversation, {
        labelAssignments: labelAssignmentsMap.get(conversation.conversationId) ?? [],
        client: getClient(conversation.requester?.participantIdentity),
        platformUser: getAgent(conversation.requester?.participantIdentity),
        assignee: getAgent(conversation.conversationAssigneeId),
        partner: rawPartnerMap?.get(conversation.partnerId) || null,
        conversationAssigneeGroup: rawConversationAssigneeGroupsMap?.get(
          conversation.conversationAssigneeGroupId,
        ),
        activeUserIds: activeUserRecordsIds,
        unseenCount:
          conversationAggregationsMap.get(conversation.conversationId)?.unseenCount ?? 0,
        showPrivateLabels: !!canAccessToPrivateLabels,
      });

      // since relation of friendlyId vs conversationId will never change
      // we can safely store it in the localstorage to be able to map friendly id from the url
      // into uuid and get conversation from the apollo cache. We could change conversation cache
      // field identifier to [partnerId, workspaceConversationId] that works great for fetching
      // but would introduce some overhead of cache updates and ws subscriptions.
      if (!friendlyIdToUuidMap[formattedConversation.friendlyId]) {
        addFriendlyIdToUuidItem(
          formattedConversation.conversationId,
          formattedConversation.friendlyId,
        );
      }

      // carry the raw blob onto the formatted row so the grid can decode custom attribute columns
      // from the same field the side panel editor reads/writes.
      if (conversation.customAttributes) {
        formattedConversation.customAttributes = conversation.customAttributes;
      }

      return formattedConversation;
    });
  }, [
    labelAssignmentsMap,
    usersLoading,
    getAgent,
    getClient,
    rawConversationAssigneeGroupsMap,
    rawPartnerMap,
    variables?.params?.sorting,
    data?.getConversation,
    activeUserRecordsIds,
    conversationAggregationsMap,
    canAccessToPrivateLabels,
    friendlyIdToUuidMap,
    addFriendlyIdToUuidItem,
  ]);

  const loadMoreParams: QueryParams =
    variables?.params ??
    (
      queryOptions.variables as {
        params: QueryParams;
      }
    ).params;

  const { loadMore, hasReachedEnd } = useLoadMore<GetConversationQuery>({
    offset: conversations.length,
    fetchMore,
    params: loadMoreParams,
    deduplicationPredicate: isSameRecords,
  });

  const totalRecords = data?.getConversation?.metadata?.totalRecords ?? 0;

  return {
    labelAssignmentsMap,
    conversations,
    client,
    loadMore,
    totalRecords,
    hasMore: data?.getConversation.metadata?.totalRecords
      ? totalRecords > (data?.getConversation?.records?.length ?? 0)
      : !hasReachedEnd,
    isLoadingMore: networkStatus === NetworkStatus.fetchMore,
    refetch,
    loading: Boolean(conversationsLoading || usersLoading),
    cacheManager,
    ...rest,
  };
};

const CREATE_CONVERSATION_MUTATION = gql`
  mutation CreateConversation(
    $conversationId: String
    $partnerId: Int!
    $subject: String!
    $participants: [SubmitConversationDataParticipantInput]
    $type: String
    $urgencyId: String!
    $groupId: String
    $associatedAddresses: [String]
    $macroActions: String
    $activeChannels: [ActiveChannelsInput]
    $isNewConversation: Boolean!
    $enqueue: Boolean!
  ) {
    submitConversationData(
      params: {
        partnerId: $partnerId
        conversation: {
          conversationId: $conversationId
          subject: $subject
          conversationAssigneeGroupId: $groupId
          type: $type
          conversationUrgencyId: $urgencyId
        }
        associatedAddresses: $associatedAddresses
        participants: $participants
        macroActions: $macroActions
        activeChannels: $activeChannels
        isNewConversation: $isNewConversation
        enqueue: $enqueue
      }
    ) {
      conversation {
        ...CreateConversationQueryFields
      }
      message {
        id
      }
    }
  }
  ${CREATE_CONVERSATION_QUERY_FIELDS}
`;

const SUBMIT_CONVERSATION_DATA_MUTATION = gql`
  mutation SubmitConversationData(
    $conversationId: String
    $partnerId: Int!
    $subject: String!
    $messageBody: String
    $messageAttachments: [File]
    $messageIsPrivate: Boolean
    $mentions: [EntityIdentifier]
    $conversationMessageId: String
    $participants: [SubmitConversationDataParticipantInput]
    $type: String
    $urgencyId: String!
    $agentId: String
    $groupId: String
    $associatedAddresses: [String]
    $macroActions: String
    $activeChannels: [ActiveChannelsInput]
    $ignoreActiveChannelsFor: [IgnoreActiveChannelsInfo]
    $isNewConversation: Boolean!
    $enqueue: Boolean!
    $conversationStatusId: String
    $snoozeUntil: String
  ) {
    submitConversationData(
      params: {
        partnerId: $partnerId
        conversation: {
          conversationId: $conversationId
          subject: $subject
          conversationAssigneeId: $agentId
          conversationAssigneeGroupId: $groupId
          type: $type
          conversationUrgencyId: $urgencyId
          conversationStatusId: $conversationStatusId
          snoozeUntil: $snoozeUntil
        }
        message: {
          body: $messageBody
          attachments: $messageAttachments
          isPrivate: $messageIsPrivate
          mentions: $mentions
          conversationMessageId: $conversationMessageId
        }
        associatedAddresses: $associatedAddresses
        participants: $participants
        macroActions: $macroActions
        activeChannels: $activeChannels
        ignoreActiveChannelsFor: $ignoreActiveChannelsFor
        isNewConversation: $isNewConversation
        enqueue: $enqueue
      }
    ) {
      conversation {
        ...SubmitConversationQueryFields
      }
      message {
        id
      }
    }
  }
  ${SUBMIT_CONVERSATION_QUERY_FIELDS}
`;

const SNOOZE_CONVERSATION_MUTATION = gql`
  mutation SnoozeConversation($conversationId: String!, $snoozeUntil: String!) {
    snoozeConversation(
      params: { conversationId: $conversationId, snoozeUntil: $snoozeUntil }
    ) {
      success
      error
      conversationId
    }
  }
`;

const MERGE_CONVERSATION_MUTATION = gql`
  mutation MergeConversation(
    $conversationOriginId: String!
    $friendlyConversationOriginId: String!
    $conversationTargetId: String!
    $friendlyConversationTargetId: String!
    $user: MergeConversationUserDataInput!
  ) {
    mergeConversation(
      params: {
        conversationOriginId: $conversationOriginId
        friendlyConversationOriginId: $friendlyConversationOriginId
        conversationTargetId: $conversationTargetId
        friendlyConversationTargetId: $friendlyConversationTargetId
        user: $user
      }
    ) {
      success
      error
    }
  }
`;

export interface UseConversationParams {
  conversationId?: string;
  partnerId?: number | null;
  limit?: number;
}

type UseConversationResult = Pick<
  ReturnType<typeof useConversationMessages>,
  'messages' | 'participants' | 'loading' | 'loadMore' | 'hasMore' | 'isLoadingMore'
> & {
  submitConversationData: (input: ConversationDataForSubmission) => Promise<unknown>;
  updateMessage: ReturnType<typeof useConversationMessageUpdate>['updateMessage'];
  editMessage: ReturnType<typeof useConversationPrivateMessageUpdate>['updatePrivateMessage'];
  isSendingMessage: boolean;
  isUpdatingMessage: boolean;
  isEditMessageLoading: boolean;
};

export const useConversation = (params: UseConversationParams = {}): UseConversationResult => {
  const { showErrorAlert, showSuccessAlert } = useSnackbar();
  const { user } = useCurrentUserProfile();

  const { conversationId, partnerId, limit } = params;
  const {
    messages,
    participants,
    loading: messagesLoading,
    offset,
    loadMore,
    hasMore,
    isLoadingMore,
  } = useConversationMessages({
    conversationId,
    partnerId,
    user,
    limit,
  });

  const [submitConversationMutation, { loading: isSendingMessage, client }] = useMutation<{
    submitConversationData: {
      conversation: {
        id: string;
        conversationId: string;
        workspaceConversationId: number;
        partnerId: number;
      };
      message: {
        id: string;
      };
    };
  }>(SUBMIT_CONVERSATION_DATA_MUTATION, {
    onError: () =>
      showErrorAlert('Something went wrong sending conversation message, please try again'),
  });

  const submitConversationData = useCallback(
    (input: ConversationDataForSubmission) => {
      const createdAt = new Date().toISOString();
      /*
        To generate optimistic cache update, we need to provide a temporary id for the message,
        because every cached entity should have one.
        As soon as the mutation is resolved, the server will provide a real id for the message.

        So `update()` method below is called twice:
        - first time with the object from `optimisticResponse` field as second argument, and we use
        this "fake" object with temporary id to update the cache and provide user with instant UI feedback;
        - second time with the real object from the server response, and we use it to update the cached object
        with the real id.
      */
      const tempId = getTempId(createdAt);
      const optimisticMessageId = input.message.conversationMessageId || tempId;

      const preparedParticipants = input.participants.map((participant) => {
        const { name: _, ...data } = participant;
        return data;
      });

      const statusOverride = input.conversation.conversationStatusId;
      const snoozeUntilOverride = input.conversation.snoozeUntil;

      return submitConversationMutation({
        variables: {
          conversationId,
          partnerId: input.conversation.partnerId,
          participants: preparedParticipants,
          messageBody: input.message.body,
          messageAttachments: input.message.attachments,
          messageIsPrivate: input.message.isPrivate,
          mentions: input.message.mentions,
          conversationMessageId: input.message.conversationMessageId,
          subject: input.conversation.subject,
          type: input.conversation.type,
          urgencyId: input.conversation.urgency,
          activeChannels: input.activeChannels,
          ignoreActiveChannelsFor: input.ignoreActiveChannelsFor,
          macroActions: input.macroActions,
          isNewConversation: false,
          enqueue: true,
          conversationStatusId: statusOverride ?? undefined,
          snoozeUntil: snoozeUntilOverride ?? undefined,
        },
        optimisticResponse: {
          submitConversationData: {
            conversation: {
              id: conversationId!,
              workspaceConversationId: input.conversation.workspaceConversationId!,
              partnerId: input.conversation.partnerId!,
              conversationId: conversationId!,
            },
            message: {
              id: optimisticMessageId,
            },
          },
        },
        update: (cache, { data: response }, { variables }) => {
          const messageId = response?.submitConversationData.message.id;
          const shouldUpdateConversationAttachments = !!input.message.attachments?.length;

          cache.updateQuery<GetConversationMessageQuery, QueryVariables>(
            {
              query: GET_CONVERSATION_MESSAGES_QUERY,
              variables: getConversationMessagesQueryVariables({
                filters: [
                  {
                    attribute: 'conversationId',
                    operator: '=',
                    value: conversationId!,
                  },
                ],
                offset,
              }),
            },
            (existingData) => {
              if (!existingData) return null;

              const { getConversationMessage } = existingData;

              return {
                ...existingData,
                getConversationMessage: {
                  ...getConversationMessage,
                  records: [
                    {
                      __typename: 'ConversationMessage',
                      id: messageId ?? optimisticMessageId,
                      conversationMessageId: messageId ?? optimisticMessageId,
                      body: input.message.body,
                      isPrivate: input.message.isPrivate,
                      createdAt,
                      partnerId: input.conversation.partnerId,
                      conversationId,
                      editedAt: null,
                      senderChannel: {
                        __typename: 'ConversationChannel',
                        // Sender channel id is not required for correct display of optimistic update,
                        // so no need to provide this information except channelType.id.
                        id: '',
                        channelType: {
                          __typename: 'ConversationChannelType',
                          id: 'chat',
                          title: 'Chat',
                        },
                        conversationParticipant: {
                          id: user?.id,
                          __typename: 'ConversationParticipant',
                          participantEntity: ConversationParticipantEntity.PlatformUser,
                          participantIdentity: user?.userId,
                          partnerId: user?.partnerId,
                          conversationParticipantId: user?.id,
                        },
                      },
                      attachments: input.message.attachments?.map((attachment) => ({
                        id: `${optimisticMessageId}-${attachment.url.split('/').at(-1)}`,
                        conversationAttachmentId: `${optimisticMessageId}-${attachment.url.split('/').at(-1)}`,
                        conversationMessageId: messageId ?? optimisticMessageId,
                        conversationId,
                        partnerId: input.conversation.partnerId,
                        size: attachment.size,
                        contentType: attachment.type,
                        fileName: attachment.name,
                        fileKey: attachment.url.split('/').at(-1),
                        createdAt,
                        callTranscription: null,
                      })),
                      incomingConversationEmail: null,
                      messageDispatches: [],
                      // The real conversation_sms row appears once the dispatcher picks the message
                      // up; tagging the optimistic record keeps the bubble inside the session thread.
                      conversationSmsRecords: input.message.smsSessionId
                        ? [
                            {
                              __typename: 'ConversationSms',
                              id: `${optimisticMessageId}-sms`,
                              conversationSmsSessionId: input.message.smsSessionId,
                              direction: ConversationSmsDirection.Outgoing,
                              externalMessageId: null,
                            },
                          ]
                        : [],
                    },
                    ...getConversationMessage.records,
                  ],
                },
              } as GetConversationMessageQuery;
            },
          );

          updateCachedConversation({
            cache,
            conversationId,
            props: {
              lastMessageReceivedAt: new Date().toISOString(),
              conversationId,
              ...(variables?.conversationStatusId
                ? {
                    conversationStatusId: variables.conversationStatusId as ConversationStatus,
                  }
                : {}),
              ...(variables?.snoozeUntil !== undefined
                ? { snoozeUntil: variables.snoozeUntil as string }
                : {}),
            },
          });

          if (
            shouldUpdateConversationAttachments &&
            conversationId &&
            !isNullish(input.conversation.partnerId)
          ) {
            const partnerId = input.conversation.partnerId;

            emitAppEvent(AppEvents.ConversationAttachmentsChanged, {
              conversationId,
              attachments:
                input.message.attachments?.map((attachment) => {
                  const fileKey = attachment.url.split('/').at(-1) ?? attachment.name;
                  const attachmentId = `${optimisticMessageId}-${fileKey}`;

                  return {
                    __typename: 'ConversationAttachment' as const,
                    id: attachmentId,
                    conversationAttachmentId: attachmentId,
                    conversationId,
                    partnerId,
                    fileKey,
                    fileName: attachment.name,
                    contentType: attachment.type,
                    size: attachment.size,
                    createdAt,
                  };
                }) ?? [],
            });
          }
        },
        onCompleted: async (data) => {
          const realId = data?.submitConversationData.message.id;

          if (realId && !input.message.isPrivate) {
            showSuccessAlert('Message sent');
          }

          if (!realId || input.message.isPrivate) return;

          await new Promise((res) => setTimeout(res, 2000));

          // fetch just the one message, network‐only
          const { data: fresh } = await client.query<
            GetConversationMessageQuery,
            QueryVariables
          >({
            query: GET_CONVERSATION_MESSAGES_QUERY,
            variables: getConversationMessagesQueryVariables({
              filters: [
                { attribute: 'conversationId', operator: '=', value: conversationId! },
                { attribute: 'conversationMessageId', operator: '=', value: realId },
              ],
              offset: 0,
              limit: 1,
            }),

            fetchPolicy: 'network-only',
          });

          const freshMsg = fresh.getConversationMessage.records[0];
          if (!freshMsg) return;

          const { conversationSmsRecords, ...settledFields } = freshMsg;

          const fields = generateFieldModifiersMap(
            conversationSmsRecords?.length
              ? { ...settledFields, conversationSmsRecords }
              : settledFields,
          );

          client.cache.modify({
            id: client.cache.identify({
              __typename: 'ConversationMessage',
              id: realId,
            }),
            fields: {
              ...fields,
            },
          });
        },
      });
    },
    [
      submitConversationMutation,
      conversationId,
      offset,
      user?.id,
      user?.userId,
      user?.partnerId,
      client,
      showSuccessAlert,
    ],
  );

  const { updateMessage, loading: isUpdatingMessage } = useConversationMessageUpdate();
  const { updatePrivateMessage, loading: isUpdatingPrivateMessage } =
    useConversationPrivateMessageUpdate();

  return {
    messages,
    participants,
    loading: messagesLoading,
    submitConversationData,
    updateMessage,
    editMessage: updatePrivateMessage,
    isSendingMessage,
    isUpdatingMessage,
    isEditMessageLoading: isUpdatingPrivateMessage,
    loadMore,
    hasMore,
    isLoadingMore,
  };
};

export interface CreateConversationValues {
  conversationId: string;
  partner: Pick<Partner, 'id'> & Partial<Pick<Partner, 'supportEmail' | 'membershipEmail'>>;
  subject: string;
  requesterId: string;
  agentId: string;
  type: string;
  urgencyId: string;
  groupId: string;
  address?: string;
  requesterEmail?: string;
  requesterEntity: ConversationParticipantEntity;
  participationMode: ConversationParticipationMode;
}

export interface SnoozeConversationValues {
  conversationId: string;
  snoozeUntil: string;
}

export interface MergeConversationValues {
  conversationOriginId: string;
  friendlyConversationOriginId: string;
  conversationTargetId: string;
  friendlyConversationTargetId: string;
  user: MappedUserData | null;
}

interface CreateConversationResult {
  id: string;
  conversationId: string;
  workspaceConversationId: number;
  partnerId: number;
}

export const useCreateConversation = (): {
  createConversation: (
    values: CreateConversationValues,
  ) => Promise<CreateConversationResult | undefined>;
  isLoading: boolean;
} => {
  const { showErrorAlert } = useNotifications();
  const invalidateSiteConversations = useInvalidateSiteConversations();

  const [submitConversationMutation, { loading }] = useMutation<{
    submitConversationData: {
      conversation: {
        id: string;
        conversationId: string;
        workspaceConversationId: number;
        partnerId: number;
      };
      message: {
        id: string;
      };
    };
  }>(CREATE_CONVERSATION_MUTATION, {
    onError: () =>
      showErrorAlert('Something went wrong creating conversation, please try again'),
  });

  const createConversation = useCallback(
    async (values: CreateConversationValues) => {
      const {
        conversationId,
        partner,
        subject,
        requesterId,
        requesterEmail,
        requesterEntity,
        agentId,
        type,
        urgencyId,
        groupId,
        address,
        participationMode,
      } = values;

      // we should always create chat channel for the client.
      const clientChannels: SubmitConversationDataChannel[] = [
        {
          channelType: ConversationChannelType.Chat,
          internalIdentifier: ConversationChatApp.Portal,
          externalIdentifier: getChatIdentifier(
            ConversationChatApp.ClientGateway,
            requesterId,
          ),
        },
      ];

      const internalIdentifier = getPartnerEmail(groupId, partner);
      if (requesterEmail && internalIdentifier) {
        clientChannels.push({
          channelType: ConversationChannelType.Email,
          internalIdentifier,
          externalIdentifier: requesterEmail,
        });
      }

      const participants: SubmitConversationDataParticipant[] = [
        ...(agentId !== requesterId
          ? [
              {
                entity: requesterEntity,
                identity: requesterId,
                isRequester: true,
                channels: clientChannels,
              },
            ]
          : []),
        {
          entity: ConversationParticipantEntity.PlatformUser,
          identity: agentId,
          isRequester: agentId === requesterId,
          channels: [
            {
              channelType: ConversationChannelType.Chat,
              internalIdentifier: 'portal',
              externalIdentifier: getChatIdentifier(ConversationChatApp.Portal, agentId),
              useToSendMessage: true,
              participationMode,
            },
          ],
        },
      ];

      const { data } = await submitConversationMutation({
        variables: {
          conversationId,
          partnerId: partner?.id,
          participants,
          subject,
          agentId,
          type,
          urgencyId,
          groupId,
          associatedAddresses: address ? [address] : undefined,
          isNewConversation: true,
          enqueue: false,
        },
      });

      const createdConversation = data?.submitConversationData?.conversation;

      if (createdConversation && address) {
        // The conversation↔site link is a relation the WS create event doesn't carry, so the
        // Associated Conversations sidebar for this site stays stale until a page reload. Refresh
        // just the lists scoped to this site, leaving other conversation queries untouched.
        invalidateSiteConversations(address);
      }

      return createdConversation;
    },
    [submitConversationMutation, invalidateSiteConversations],
  );

  return { createConversation, isLoading: loading };
};

interface SnoozeConversationResult {
  error: string | null;
  success: boolean;
  conversationId: string | null;
}

export const useSnoozeConversation = (): {
  snoozeConversation: (
    values: SnoozeConversationValues,
  ) => Promise<SnoozeConversationResult | undefined>;
  isLoading: boolean;
} => {
  const { showErrorAlert } = useNotifications();

  const [snoozeConversationMutation, { loading }] = useMutation<{
    snoozeConversation: {
      error: string | null;
      success: boolean;
      conversationId: string | null;
    };
  }>(SNOOZE_CONVERSATION_MUTATION, {
    onError: () =>
      showErrorAlert('Something went wrong snoozing conversation, please try again'),
    update: (cache, { data: response }, { variables }) => {
      const updated = response?.snoozeConversation;
      if (!updated?.conversationId) return;

      updateCachedConversation({
        cache,
        props: {
          conversationStatusId: ConversationStatus.Snoozed,
          conversationId: updated.conversationId,
          snoozeUntil: variables?.snoozeUntil as string,
        },
        conversationId: updated.conversationId,
      });
    },
    optimisticResponse: ({ conversationId, snoozeUntil }) => ({
      snoozeConversation: {
        success: true,
        snoozeUntil: snoozeUntil as string,
        conversationId: conversationId as string,
        error: null,
      },
    }),
  });

  const snoozeConversation = useCallback(
    async (values: SnoozeConversationValues) => {
      const { conversationId, snoozeUntil } = values;

      const { data } = await snoozeConversationMutation({
        variables: {
          conversationId,
          snoozeUntil,
        },
      });

      return data?.snoozeConversation;
    },
    [snoozeConversationMutation],
  );

  return { snoozeConversation, isLoading: loading };
};

interface MergeConversationResult {
  error: string | null;
  success: boolean;
}

export const useMergeConversation = (): {
  mergeConversation: (
    values: MergeConversationValues,
  ) => Promise<MergeConversationResult | undefined>;
  isLoading: boolean;
} => {
  const { showErrorAlert } = useNotifications();

  const [mergeConversationMutation, { loading }] = useMutation<{
    mergeConversation: {
      error: string | null;
      success: boolean;
    };
  }>(MERGE_CONVERSATION_MUTATION, {
    onError: () =>
      showErrorAlert('Something went wrong merging conversations, please try again'),
  });

  const mergeConversation = useCallback(
    async (values: MergeConversationValues) => {
      const {
        conversationOriginId,
        friendlyConversationOriginId,
        conversationTargetId,
        friendlyConversationTargetId,
        user,
      } = values;

      const { data } = await mergeConversationMutation({
        variables: removeNotDefinedProperties({
          conversationOriginId,
          friendlyConversationOriginId,
          conversationTargetId,
          friendlyConversationTargetId,
          user: user
            ? {
                id: user.id,
                userId: user.userId,
                partnerId: user.partnerId,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
              }
            : undefined,
        }),
        update: (cache) => {
          // Original conversation is soft-deleted after merging and it should not be available
          // in UI anymore.
          const id = cache.identify({
            __typename: 'Conversation',
            id: conversationOriginId,
          });
          cache.evict({ id });
          cache.gc();
        },
      });

      return data?.mergeConversation;
    },
    [mergeConversationMutation],
  );

  return { mergeConversation, isLoading: loading };
};

function getTempId(date: string): string {
  return `temp-id:${date}`;
}

export function updateCachedConversation({
  cache,
  props,
  conversationId,
  extraFields,
}: {
  cache: ApolloCache<unknown>;
  props: Partial<Conversation>;
  conversationId?: string;
  extraFields?: Parameters<ApolloCache<Conversation>['modify']>[0]['fields'];
}): void {
  if (!conversationId) {
    return;
  }

  const id = cache.identify({
    __typename: 'Conversation',
    id: conversationId,
  });

  const fields = generateFieldModifiersMap(props);

  cache.modify({
    id,
    fields: {
      ...fields,
      ...(extraFields ?? {}),
    },
  });
}

export function getCachedConversation(
  cache: ApolloCache<unknown>,
  conversationId?: string,
): ConversationRaw | null | undefined {
  if (!conversationId) {
    return;
  }

  const fragment = gql`
    fragment CurrentConversation on Conversation {
      ...ConversationFields
    }
    ${CONVERSATION_FIELDS}
  `;

  const id = cache.identify({
    __typename: 'Conversation',
    id: conversationId,
  });

  return cache.readFragment<ConversationRaw>({
    id,
    fragment,
    fragmentName: 'CurrentConversation',
  });
}

export function getCachedConversationMessage(
  cache: ApolloCache<unknown>,
  conversationMessageId?: string,
): ConversationRaw | null | undefined {
  if (!conversationMessageId) {
    return;
  }

  const fragment = gql`
    fragment CurrentMessage on ConversationMessage {
      id
    }
  `;

  const id = cache.identify({
    __typename: 'ConversationMessage',
    id: conversationMessageId,
  });

  return cache.readFragment<ConversationRaw>({
    id,
    fragment,
    fragmentName: 'CurrentMessage',
  });
}

export const conversationTypeOptions = [
  {
    label: 'Support Ticket',
    id: ConversationType.SUPPORT_TICKET,
    value: ConversationType.SUPPORT_TICKET,
    icon: 'headphones',
    iconLibrary: 'lucide' as const,
  },
  {
    label: 'Member Services',
    id: ConversationType.MEMBER_SERVICES,
    value: ConversationType.MEMBER_SERVICES,
    icon: 'id-card',
    iconLibrary: 'lucide' as const,
  },
  {
    label: 'Non-Support Event',
    id: ConversationType.NON_SUPPORT_EVENT,
    value: ConversationType.NON_SUPPORT_EVENT,
    icon: 'messages-square',
    iconLibrary: 'lucide' as const,
  },
];

export const conversationTypeOptionsMap = new Map(
  conversationTypeOptions.map((option) => [option.id, option]),
);

function getPartnerEmail(
  groupId: string,
  partner: CreateConversationValues['partner'],
): string | undefined {
  return groupId === MEMBERSHIP_SUPPORT_GROUP_ID
    ? partner.membershipEmail
    : partner.supportEmail;
}
